import React, { useEffect, useRef, useState } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Dialog } from '../../components/ui/Dialog';
import { useLanguage } from '../../lib/i18n';
import { captureAvatarFrame } from './avatarImage';

interface CameraCaptureDialogProps {
  open: boolean;
  onClose: () => void;
  onCapture: (dataUrl: string) => void;
}

/**
 * Profil fotoğrafını kameradan çeker. Önizleme ayna gibi (kendini doğal
 * görmek için), kaydedilen kare ayna değil. Pencere kapanınca kamera da
 * kapanıyor — ışık açık kalmasın.
 */
export const CameraCaptureDialog: React.FC<CameraCaptureDialogProps> = ({ open, onClose, onCapture }) => {
  const { t } = useLanguage();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    setIsReady(false);

    navigator.mediaDevices
      .getUserMedia({ video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' } })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          void videoRef.current.play().catch(() => {});
        }
        setIsReady(true);
      })
      .catch((err: DOMException) => {
        if (cancelled) return;
        if (err.name === 'NotAllowedError') {
          setError(t('Kamera izni verilmedi.', 'Camera permission was denied.'));
        } else if (err.name === 'NotFoundError' || err.name === 'OverconstrainedError') {
          setError(t('Kamera bulunamadı.', 'No camera was found.'));
        } else {
          setError(t('Kamera açılamadı.', 'Could not open the camera.'));
        }
      });

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, [open, t]);

  const take = () => {
    const video = videoRef.current;
    if (!video) return;
    try {
      onCapture(captureAvatarFrame(video));
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('Fotoğraf çek', 'Take a photo')}
      className="max-w-md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Vazgeç', 'Cancel')}
          </Button>
          <Button onClick={take} disabled={!isReady || Boolean(error)}>
            <Camera className="size-4" />
            {t('Çek', 'Capture')}
          </Button>
        </>
      }
    >
      {error ? (
        <p role="alert" className="py-6 text-center text-sm text-destructive">
          {error}
        </p>
      ) : (
        <div className="relative aspect-square w-full overflow-hidden rounded-xl bg-muted">
          <video ref={videoRef} muted playsInline className="size-full -scale-x-100 object-cover" />
          {!isReady && (
            <span className="absolute inset-0 flex items-center justify-center text-muted-foreground">
              <Loader2 className="size-5 animate-spin" />
            </span>
          )}
        </div>
      )}
    </Dialog>
  );
};
